import {
  Body, Controller, Delete, ForbiddenException, Get,
  HttpCode, Param, Patch, Post, Put, Req, UseGuards,
} from '@nestjs/common';
import { GuardedRequest, AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { ThemesService } from './themes.service';
import { StoreContextService } from '../tenancy/store-context.service';
import { CreateThemeRequest, UpdateThemeRequest, ApplyThemeRequest } from '@cart/shared-types';

const PLATFORM_ROLES = ['admin', 'super_admin', 'platform_admin'];

// store/theme is intentionally outside the @UseGuards class so the public
// storefront can fetch the theme without a JWT token.
@Controller('store')
export class StorefrontThemeController {
  constructor(
    private readonly themesService: ThemesService,
    private readonly storeContext: StoreContextService,
  ) {}

  @Get('theme')
  getTheme() {
    const storeId = this.storeContext.storeId();
    return this.themesService.resolveStoreTheme(storeId);
  }
}

@Controller()
@UseGuards(AuthGuard, DemoModeGuard)
export class ThemesController {
  constructor(
    private readonly themesService: ThemesService,
    private readonly storeContext: StoreContextService,
  ) {}

  // ── Platform: theme library management (super_admin / platform_admin) ─────

  @Get('platform/themes')
  listAll(@Req() req: GuardedRequest) {
    this.requirePlatformRole(req);
    return this.themesService.listAll();
  }

  @Get('platform/themes/:id')
  getById(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.themesService.getById(id);
  }

  @Post('platform/themes')
  create(@Body() dto: CreateThemeRequest, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.themesService.create(dto, req.user.id);
  }

  @Put('platform/themes/:id')
  update(
    @Param('id') id: string,
    @Body() dto: UpdateThemeRequest,
    @Req() req: AuthenticatedRequest,
  ) {
    this.requirePlatformRole(req);
    return this.themesService.update(id, dto, req.user.id);
  }

  @Patch('platform/themes/:id/publish')
  publish(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.themesService.setPublished(id, true, req.user.id);
  }

  @Patch('platform/themes/:id/unpublish')
  unpublish(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.themesService.setPublished(id, false, req.user.id);
  }

  @Delete('platform/themes/:id')
  @HttpCode(200)
  remove(@Param('id') id: string, @Req() req: AuthenticatedRequest) {
    this.requirePlatformRole(req);
    return this.themesService.remove(id, req.user.id);
  }

  // ── Merchant: browse published themes ────────────────────────────────────

  @Get('themes')
  listPublished() {
    return this.themesService.listPublished();
  }

  // ── Merchant: get / apply theme on their store ────────────────────────────

  @Get('merchant/stores/:storeId/theme')
  getStoreTheme(@Param('storeId') storeId: string) {
    return this.themesService.getStoreTheme(storeId);
  }

  @Patch('merchant/stores/:storeId/theme')
  applyTheme(
    @Param('storeId') storeId: string,
    @Body() dto: ApplyThemeRequest,
    // Inside ThemesController, which carries a class-level
    // @UseGuards(AuthGuard, DemoModeGuard) — so the user is guaranteed here.
    @Req() req: GuardedRequest,
  ) {
    return this.themesService.applyToStore(storeId, dto.themeId, req.user.id, req.user.role);
  }

  /**
   * Asserts, rather than returns: callers read `req.user` straight after,
   * and this is what makes that safe without a non-null assertion at every
   * call site. The runtime check is unchanged — it always threw when the
   * role was missing, which necessarily means `user` was present.
   */
  private requirePlatformRole(req: AuthenticatedRequest): asserts req is GuardedRequest {
    if (!req.user || !PLATFORM_ROLES.includes(req.user.role)) {
      throw new ForbiddenException('Platform admin access required');
    }
  }
}
